import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Chip,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Grid,
  Collapse,
  IconButton,
  TablePagination,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  useTheme,
  Switch
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EmailIcon from '@mui/icons-material/Email';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import ReplyIcon from '@mui/icons-material/Reply';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SaveIcon from '@mui/icons-material/Save';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { API_URL } from '../config';

// Define the IconType type to make it consistent across the file
type IconType = 'default' | 'agent' | 'emoji-smile' | 'emoji-wave' | 'question' | 'support' | 'custom';

interface Chatbot {
  _id: string;
  name: string;
  welcomeMessage: string;
  primaryColor: string;
  position: 'bottom-right' | 'bottom-left';
  iconType?: IconType;
  customIconUrl?: string;
  widgetShape?: 'circle' | 'bubble';
  fontFamily?: 'Roboto' | 'Open Sans' | 'Lato' | 'Montserrat' | 'Playfair Display' | 'Poppins' | 'Inter';
  isActive: boolean;
  businessId: string;
  settings: {
    allowFileUpload: boolean;
    businessHours: {
      enabled: boolean;
      start: string;
      end: string;
      timezone: string;
      allowLeaveMessageOutsideHours: boolean;
    };
    defaultQuickReplies: {
      enabled: boolean;
      replies: string[];
      maxDisplay: number;
    };
    allowLeaveMessage: boolean;
    soundSettings?: {
      enabled: boolean;
      messageSentSound: string;
      messageReceivedSound: string;
    };
    aiSettings: {
      enabled: boolean;
      customInstructions: string;
      model: string;
      temperature: number;
      maxTokens: number;
    };
  };
  trialStatus: {
    isInTrial: boolean;
    trialEndDate: string;
    trialStartDate: string;
    firstApiCallDate: string | null;
  };
}

interface User {
  _id: string;
  email: string;
  role: 'admin' | 'business';
  businessName: string;
  website: string;
  chatbots: Chatbot[];
}

interface ContactMessage {
  _id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: 'new' | 'read' | 'replied';
  createdAt: string;
}

interface KeywordResponse {
  _id: string;
  keywords: string[];
  response: string;
  responseVariations: string[];
  variables: Array<{
    name: string;
    defaultValue: string;
  }>;
  context: 'general' | 'greeting' | 'farewell' | 'help' | 'pricing' | 'hours' | 'location' | 'custom';
  priority: number;
  isActive: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface CreateUserFormData {
  email: string;
  password: string;
  businessName: string;
  website: string;
}

interface ChatbotFormData {
  name: string;
  welcomeMessage: string;
  primaryColor: string;
  position: 'bottom-right' | 'bottom-left';
  iconType: IconType;
  customIconUrl?: string;
  widgetShape?: 'circle' | 'bubble';
  fontFamily?: 'Roboto' | 'Open Sans' | 'Lato' | 'Montserrat' | 'Playfair Display' | 'Poppins' | 'Inter';
  isActive: boolean;
  businessId?: string;
  trialStatus: {
    isInTrial: boolean;
    trialEndDate: string;
    trialStartDate: string;
    firstApiCallDate: string | null;
  };
  settings: {
    allowFileUpload: boolean;
    businessHours: {
      enabled: boolean;
      start: string;
      end: string;
      timezone: string;
      allowLeaveMessageOutsideHours: boolean;
    };
    defaultQuickReplies: {
      enabled: boolean;
      replies: string[];
      maxDisplay: number;
    };
    allowLeaveMessage: boolean;
    soundSettings: {
      enabled: boolean;
      messageSentSound: string;
      messageReceivedSound: string;
    };
    aiSettings: {
      enabled: boolean;
      customInstructions: string;
      model: string;
      temperature: number;
      maxTokens: number;
    };
  };
}

const AdminDashboard: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [chatbots, setChatbots] = useState<Chatbot[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedBusiness, setSelectedBusiness] = useState<User | null>(null);
  const [selectedChatbot, setSelectedChatbot] = useState<Chatbot | null>(null);
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [openChatbotDialog, setOpenChatbotDialog] = useState(false);
  const [openCreateUserDialog, setOpenCreateUserDialog] = useState(false);
  const [openCreateChatbotDialog, setOpenCreateChatbotDialog] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [userFormData, setUserFormData] = useState({
    businessName: '',
    website: ''
  });
  const [chatbotFormData, setChatbotFormData] = useState<ChatbotFormData>({
    name: '',
    welcomeMessage: '',
    primaryColor: '#1976d2',
    position: 'bottom-right',
    iconType: 'default',
    customIconUrl: '',
    widgetShape: 'circle',
    fontFamily: 'Roboto',
    isActive: true,
    trialStatus: {
      isInTrial: true,
      trialEndDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      trialStartDate: new Date().toISOString().split('T')[0],
      firstApiCallDate: null
    },
    settings: {
      allowFileUpload: true,
      businessHours: {
        enabled: true,
        start: '09:00',
        end: '17:00',
        timezone: 'UTC',
        allowLeaveMessageOutsideHours: true
      },
      defaultQuickReplies: {
        enabled: false,
        replies: [] as string[],
        maxDisplay: 3
      },
      allowLeaveMessage: true,
      soundSettings: {
        enabled: false,
        messageSentSound: '',
        messageReceivedSound: ''
      },
      aiSettings: {
        enabled: false,
        customInstructions: '',
        model: 'gpt-3.5-turbo',
        temperature: 0.7,
        maxTokens: 2048
      }
    }
  });
  const [createUserFormData, setCreateUserFormData] = useState<CreateUserFormData>({
    email: '',
    password: '',
    businessName: '',
    website: ''
  });
  const [createChatbotFormData, setCreateChatbotFormData] = useState<ChatbotFormData>({
    name: '',
    welcomeMessage: '',
    primaryColor: '#1976d2',
    position: 'bottom-right',
    iconType: 'default',
    customIconUrl: '',
    widgetShape: 'circle',
    fontFamily: 'Roboto',
    isActive: true,
    businessId: '',
    trialStatus: {
      isInTrial: true,
      trialEndDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      trialStartDate: new Date().toISOString().split('T')[0],
      firstApiCallDate: null
    },
    settings: {
      allowFileUpload: true,
      businessHours: {
        enabled: true,
        start: '09:00',
        end: '17:00',
        timezone: 'UTC',
        allowLeaveMessageOutsideHours: true
      },
      defaultQuickReplies: {
        enabled: false,
        replies: [] as string[],
        maxDisplay: 3
      },
      allowLeaveMessage: true,
      soundSettings: {
        enabled: false,
        messageSentSound: '',
        messageReceivedSound: ''
      },
      aiSettings: {
        enabled: false,
        customInstructions: '',
        model: 'gpt-3.5-turbo',
        temperature: 0.7,
        maxTokens: 2048
      }
    }
  });
  const [error, setError] = useState('');
  const [expandedUser, setExpandedUser] = useState<string | false>(false);
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [responses, setResponses] = useState<KeywordResponse[]>([]);
  const [newResponses, setNewResponses] = useState('');
  const [bulkEditError, setBulkEditError] = useState<string | null>(null);
  const [bulkEditSuccess, setBulkEditSuccess] = useState<string | null>(null);
  const [expandedMessage, setExpandedMessage] = useState<string | null>(null);
  const theme = useTheme();

  useEffect(() => {
    fetchUsers();
    fetchChatbots();
    fetchMessages();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/business/all`, {
        withCredentials: true
      });
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (err) {
      setError('Failed to fetch users');
    }
  };

  const fetchChatbots = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/chatbot/all`, {
        withCredentials: true
      });
      setChatbots(response.data);
    } catch (err) {
      setError('Failed to fetch chatbots');
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin-contact`, {
        withCredentials: true
      });
      setMessages(response.data);
    } catch (err) {
      setMessagesError('Failed to fetch messages');
    }
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setUserFormData({
      businessName: user.businessName,
      website: user.website
    });
    setOpenUserDialog(true);
  };

  const handleEditChatbot = (chatbot: Chatbot) => {
    setSelectedChatbot(chatbot);
    setChatbotFormData({
      name: chatbot.name,
      welcomeMessage: chatbot.welcomeMessage,
      primaryColor: chatbot.primaryColor,
      position: (chatbot.position === 'bottom-right' || chatbot.position === 'bottom-left') 
        ? chatbot.position 
        : 'bottom-right',
      iconType: chatbot.iconType || 'default',
      customIconUrl: chatbot.customIconUrl || '',
      widgetShape: chatbot.widgetShape || 'circle',
      fontFamily: chatbot.fontFamily || 'Roboto',
      isActive: chatbot.isActive,
      businessId: chatbot.businessId,
      trialStatus: {
        isInTrial: chatbot.trialStatus.isInTrial,
        trialEndDate: chatbot.trialStatus.trialEndDate ? 
          new Date(chatbot.trialStatus.trialEndDate).toISOString().split('T')[0] : 
          new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        trialStartDate: chatbot.trialStatus.trialStartDate ? 
          new Date(chatbot.trialStatus.trialStartDate).toISOString().split('T')[0] : 
          new Date().toISOString().split('T')[0],
        firstApiCallDate: chatbot.trialStatus.firstApiCallDate
      },
      settings: {
        allowFileUpload: chatbot.settings.allowFileUpload ?? true,
        businessHours: {
          enabled: chatbot.settings.businessHours?.enabled ?? true,
          start: chatbot.settings.businessHours?.start ?? '09:00',
          end: chatbot.settings.businessHours?.end ?? '17:00',
          timezone: chatbot.settings.businessHours?.timezone ?? 'UTC',
          allowLeaveMessageOutsideHours: chatbot.settings.businessHours?.allowLeaveMessageOutsideHours ?? true
        },
        defaultQuickReplies: {
          enabled: chatbot.settings.defaultQuickReplies?.enabled ?? false,
          replies: (chatbot.settings.defaultQuickReplies?.replies ?? []) as string[],
          maxDisplay: chatbot.settings.defaultQuickReplies?.maxDisplay ?? 3
        },
        allowLeaveMessage: chatbot.settings.allowLeaveMessage ?? true,
        soundSettings: chatbot.settings.soundSettings ?? {
          enabled: false,
          messageSentSound: '',
          messageReceivedSound: ''
        },
        aiSettings: {
          enabled: chatbot.settings.aiSettings?.enabled ?? false,
          customInstructions: chatbot.settings.aiSettings?.customInstructions ?? '',
          model: chatbot.settings.aiSettings?.model ?? 'gpt-3.5-turbo',
          temperature: chatbot.settings.aiSettings?.temperature ?? 0.7,
          maxTokens: chatbot.settings.aiSettings?.maxTokens ?? 2048
        }
      }
    });
    setOpenChatbotDialog(true);
  };

  const handleDeleteUser = async (id: string) => {
    try {
      await axios.delete(`${API_URL}/api/business/user/${id}`, {
        withCredentials: true
      });
      setUsers(users.filter(u => u._id !== id));
    } catch (err) {
      setError('Failed to delete user');
    }
  };

  const handleDeleteChatbot = async (id: string) => {
    try {
      await axios.delete(`${API_URL}/api/business/chatbot/${id}`, {
        withCredentials: true
      });
      setChatbots(chatbots.filter(c => c._id !== id));
    } catch (err) {
      console.error('Error deleting chatbot:', err);
      setError('Failed to delete chatbot');
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    try {
      await axios.put(`${API_URL}/api/business/profile`, {
        ...userFormData,
        userId: selectedUser._id
      }, {
        withCredentials: true
      });
      setUsers(users.map(u =>
        u._id === selectedUser._id
          ? { ...u, ...userFormData }
          : u
      ));
      setOpenUserDialog(false);
    } catch (err) {
      setError('Failed to update user');
    }
  };

  const handleUpdateChatbot = async () => {
    try {
      if (!selectedChatbot) return;
      
      // Make sure iconType is a valid value from our enum
      const validIconTypes: IconType[] = ['default', 'agent', 'emoji-smile', 'emoji-wave', 'question', 'support', 'custom'];
      
      const iconType: IconType = validIconTypes.includes(chatbotFormData.iconType as IconType) 
        ? chatbotFormData.iconType 
        : 'default';
        
      const response = await axios.put(
        `${API_URL}/api/chatbot/${selectedChatbot._id}`, 
        { 
          ...chatbotFormData,
          iconType 
        }, 
        { withCredentials: true }
      );
      
      // Update chatbots list
      setChatbots(chatbots.map(c => 
        c._id === selectedChatbot._id ? response.data : c
      ));
      
      setOpenChatbotDialog(false);
    } catch (error) {
      setError('Failed to update chatbot');
    }
  };

  const handleUserChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleChatbotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('settings.aiSettings.')) {
      const field = name.replace('settings.aiSettings.', '');
      setChatbotFormData(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          aiSettings: {
            ...prev.settings.aiSettings,
            [field]: type === 'checkbox' ? checked : value
          }
        }
      }));
    } else if (name.startsWith('settings.soundSettings.')) {
      const field = name.replace('settings.soundSettings.', '');
      setChatbotFormData(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          soundSettings: {
            ...prev.settings.soundSettings,
            [field]: type === 'checkbox' ? checked : value
          }
        }
      }));
    } else if (name.startsWith('settings.defaultQuickReplies.')) {
      const field = name.replace('settings.defaultQuickReplies.', '');
      setChatbotFormData(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          defaultQuickReplies: {
            ...prev.settings.defaultQuickReplies,
            [field]: type === 'checkbox' ? checked : value
          }
        }
      }));
    } else if (name.startsWith('settings.businessHours.')) {
      const field = name.replace('settings.businessHours.', '');
      setChatbotFormData(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          businessHours: {
            ...prev.settings.businessHours,
            [field]: type === 'checkbox' ? checked : value
          }
        }
      }));
    } else if (name.startsWith('settings.')) {
      const field = name.replace('settings.', '');
      setChatbotFormData(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          [field]: type === 'checkbox' ? checked : value
        }
      }));
    } else if (name.startsWith('trialStatus.')) {
      const field = name.replace('trialStatus.', '');
      setChatbotFormData(prev => ({
        ...prev,
        trialStatus: {
          ...prev.trialStatus,
          [field]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setChatbotFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleAccordionChange = (userId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedUser(isExpanded ? userId : false);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getBusinessName = (businessId: string) => {
    const user = users.find(u => u._id === businessId);
    return user ? user.businessName : 'Unknown Business';
  };

  const handleCreateUser = async () => {
    try {
      await axios.post(`${API_URL}/api/business/create-business`, createUserFormData, {
        withCredentials: true
      });
      setOpenCreateUserDialog(false);
      setCreateUserFormData({
        email: '',
        password: '',
        businessName: '',
        website: ''
      });
      fetchUsers(); // Refresh users list
    } catch (err) {
      console.error('Error creating user:', err);
      setError('Failed to create user');
    }
  };

  const handleCreateUserChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCreateUserFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateChatbot = async () => {
    try {
      if (!createChatbotFormData.businessId) {
        setError('Business ID is required');
        return;
      }
      
      await axios.post(`${API_URL}/api/business/chatbot`, {
        ...createChatbotFormData
      }, {
        withCredentials: true
      });
      setOpenCreateChatbotDialog(false);
      setCreateChatbotFormData({
        name: '',
        welcomeMessage: '',
        primaryColor: '#1976d2',
        position: 'bottom-right',
        iconType: 'default',
        customIconUrl: '',
        widgetShape: 'circle',
        fontFamily: 'Roboto',
        isActive: true,
        businessId: '',
        trialStatus: {
          isInTrial: true,
          trialEndDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          trialStartDate: new Date().toISOString().split('T')[0],
          firstApiCallDate: null
        },
        settings: {
          allowFileUpload: true,
          businessHours: {
            enabled: true,
            start: '09:00',
            end: '17:00',
            timezone: 'UTC',
            allowLeaveMessageOutsideHours: true
          },
          defaultQuickReplies: {
            enabled: false,
            replies: [],
            maxDisplay: 3
          },
          allowLeaveMessage: true,
          soundSettings: {
            enabled: false,
            messageSentSound: '',
            messageReceivedSound: ''
          },
          aiSettings: {
            enabled: false,
            customInstructions: '',
            model: 'gpt-3.5-turbo',
            temperature: 0.7,
            maxTokens: 2048
          }
        }
      });
      fetchChatbots(); // Refresh chatbots list
    } catch (err) {
      console.error('Error creating chatbot:', err);
      setError('Failed to create chatbot');
    }
  };

  const handleCreateChatbotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('settings.aiSettings.')) {
      const field = name.replace('settings.aiSettings.', '');
      setCreateChatbotFormData(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          aiSettings: {
            ...prev.settings.aiSettings,
            [field]: type === 'checkbox' ? checked : value
          }
        }
      }));
    } else if (name.startsWith('settings.soundSettings.')) {
      const field = name.replace('settings.soundSettings.', '');
      setCreateChatbotFormData(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          soundSettings: {
            ...prev.settings.soundSettings,
            [field]: type === 'checkbox' ? checked : value
          }
        }
      }));
    } else if (name.startsWith('settings.defaultQuickReplies.')) {
      const field = name.replace('settings.defaultQuickReplies.', '');
      setCreateChatbotFormData(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          defaultQuickReplies: {
            ...prev.settings.defaultQuickReplies,
            [field]: type === 'checkbox' ? checked : value
          }
        }
      }));
    } else if (name.startsWith('settings.businessHours.')) {
      const field = name.replace('settings.businessHours.', '');
      setCreateChatbotFormData(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          businessHours: {
            ...prev.settings.businessHours,
            [field]: type === 'checkbox' ? checked : value
          }
        }
      }));
    } else if (name.startsWith('settings.')) {
      const field = name.replace('settings.', '');
      setCreateChatbotFormData(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          [field]: type === 'checkbox' ? checked : value
        }
      }));
    } else if (name.startsWith('trialStatus.')) {
      const field = name.replace('trialStatus.', '');
      setCreateChatbotFormData(prev => ({
        ...prev,
        trialStatus: {
          ...prev.trialStatus,
          [field]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setCreateChatbotFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleUpdateMessageStatus = async (messageId: string, newStatus: 'read' | 'replied') => {
    try {
      await axios.patch(
        `${API_URL}/api/admin-contact/${messageId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      
      setMessages(messages.map(msg => 
        msg._id === messageId ? { ...msg, status: newStatus } : msg
      ));
    } catch (error) {
      console.error('Error updating message status:', error);
      setMessagesError('Failed to update message status');
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/admin-contact/${messageId}`, {
        withCredentials: true
      });
      setMessages(messages.filter(msg => msg._id !== messageId));
    } catch (error) {
      console.error('Error deleting message:', error);
      setMessagesError('Failed to delete message');
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.length >= 2) {
      const filtered = users.filter(user => 
        user.businessName.toLowerCase().includes(query.toLowerCase()) ||
        user.email.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  };

  const handleBusinessSelect = (business: User) => {
    console.log('Selecting business:', business._id, business.businessName);
    setSelectedBusiness(business);
    setSelectedChatbot(null);
    setResponses([]);
    setNewResponses('');
    setBulkEditError(null);
    setBulkEditSuccess(null);
  };

  const handleChatbotSelect = async (chatbot: Chatbot) => {
    console.log('Selecting chatbot:', chatbot._id, chatbot.name);
    if (!selectedBusiness) {
      setBulkEditError('Please select a business first');
      return;
    }
    
    if (chatbot.businessId !== selectedBusiness._id) {
      setBulkEditError('This chatbot does not belong to the selected business');
      return;
    }

    setSelectedChatbot(chatbot);
    try {
      console.log('Fetching responses for chatbot:', chatbot._id);
      const response = await axios.get(
        `${API_URL}/api/keyword-responses/${chatbot._id}`,
        {
          withCredentials: true
        }
      );
      console.log('Received responses:', response.data);
      setResponses(response.data);
      setBulkEditError(null);
    } catch (error: any) {
      console.error('Error fetching responses:', error);
      console.error('Error details:', error.response?.data);
      setBulkEditError(`Failed to fetch chatbot responses: ${error.response?.data?.message || error.message}`);
      setResponses([]);
    }
  };

  const handleSaveResponses = async () => {
    if (!selectedChatbot || !selectedBusiness) {
      setBulkEditError('Please select a business and chatbot first');
      return;
    }

    try {
      console.log('Starting bulk update process...');
      console.log('Selected chatbot:', selectedChatbot._id);
      console.log('Selected business:', selectedBusiness._id);

      // Parse the new responses text into an array of responses
      const parsedResponses = newResponses.split('\n\n')
        .filter(block => block.trim())
        .map((block, index) => {
          console.log(`Parsing block ${index + 1}:`, block);
          const lines = block.split('\n');
          
          if (lines.length < 2) {
            throw new Error(`Block ${index + 1} is missing required fields. Each block must have at least keywords and response.`);
          }
          
          // Parse keywords line (first line)
          const keywordsLine = lines[0].replace(/^Keyword:?\s*/i, '').trim();
          const keywords = keywordsLine.split(',').map(k => k.trim()).filter(k => k);
          
          if (!keywords.length) {
            throw new Error(`Block ${index + 1} has no valid keywords`);
          }
          
          // Parse response line (second line)
          const response = lines[1].replace(/^Response:?\s*/i, '').trim();
          
          if (!response) {
            throw new Error(`Block ${index + 1} has no response text`);
          }
          
          // Parse variations line (third line)
          const variationsLine = lines[2]?.replace(/^Variations:?\s*/i, '').trim() || '';
          const responseVariations = variationsLine ? variationsLine.split(',').map(v => v.trim()).filter(v => v) : [];
          
          // Parse variables line (fourth line)
          const variablesLine = lines[3]?.replace(/^Variables:?\s*/i, '').trim() || '';
          const variables = variablesLine 
            ? variablesLine.split(',')
                .map(v => v.trim())
                .filter(v => v)
                .map(v => {
                  const parts = v.split('=').map(p => p.trim());
                  return {
                    name: parts[0],
                    defaultValue: parts[1] || ''
                  };
                })
            : [];

          const parsedResponse = {
            businessId: selectedBusiness._id,
            chatbotId: selectedChatbot._id,
            keywords,
            response,
            responseVariations,
            variables,
            followUpQuestions: [],
            context: 'general' as const,
            priority: 0,
            isActive: true
          };

          console.log(`Parsed response ${index + 1}:`, parsedResponse);
          return parsedResponse;
        });

      if (!parsedResponses.length) {
        throw new Error('No valid responses found in the input');
      }

      console.log('All responses parsed successfully:', parsedResponses.length);

      // Update the responses in the database
      console.log('Sending bulk update request...');
      const updateResponse = await axios.post(
        `${API_URL}/api/keyword-responses/${selectedChatbot._id}/bulk`,
        { responses: parsedResponses },
        {
          withCredentials: true
        }
      );

      console.log('Bulk update response:', updateResponse.data);
      setBulkEditSuccess(`Successfully updated ${updateResponse.data.count} responses`);
      setBulkEditError(null);
      
      // Refresh the responses
      console.log('Refreshing responses list...');
      const response = await axios.get(
        `${API_URL}/api/keyword-responses/${selectedChatbot._id}`,
        {
          withCredentials: true
        }
      );
      setResponses(response.data);
      console.log('Responses refreshed:', response.data.length);
    } catch (error: any) {
      console.error('Error saving responses:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      let errorMessage = 'Failed to save responses: ';
      if (error.response?.data?.error) {
        errorMessage += error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Unknown error occurred';
      }
      
      setBulkEditError(errorMessage);
      setBulkEditSuccess(null);
    }
  };

  const handleExpandMessage = (messageId: string) => {
    setExpandedMessage(expandedMessage === messageId ? null : messageId);
  };

  // Function to play sound previews
  const playSound = (soundName: string) => {
    if (soundName === 'none') return;
    
    console.log(`Attempting to play sound: ${soundName}`);
    // Use API_URL to correctly reference server path
    const audio = new Audio(`${API_URL}/sounds/${soundName}.mp3`);
    audio.volume = 1.0; // Ensure volume is set to maximum
    
    // Add event listeners for debugging
    audio.addEventListener('canplaythrough', () => {
      console.log(`Sound ready to play: ${soundName}`);
      audio.play()
        .then(() => console.log(`Playing sound: ${soundName}`))
        .catch(error => console.error('Error playing sound:', error));
    });
    
    audio.addEventListener('error', (e) => {
      console.error(`Audio error (${soundName}):`, e);
    });
    
    // Force load the audio
    audio.load();
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: theme.palette.mode === 'dark' ? 'background.default' : '#f8f9fa'
    }}>
      {/* Header */}
      <Header />

      {/* Main Content */}
      <Container sx={{ flex: 1, py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Admin Dashboard
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Users" />
            <Tab label="Chatbots" />
            <Tab label="Messages" />
            <Tab label="Bulk Edit" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Users</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenCreateUserDialog(true)}
            >
              Create Business User
            </Button>
          </Box>
          {users.map((user) => (
            <Accordion
              key={user._id}
              expanded={expandedUser === user._id}
              onChange={handleAccordionChange(user._id)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h6">{user.businessName}</Typography>
                    <Chip 
                      label={user.role} 
                      color={user.role === 'admin' ? 'primary' : 'default'} 
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Button
                      startIcon={<EditIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditUser(user);
                      }}
                      sx={{ mr: 1 }}
                    >
                      Edit
                    </Button>
                    <Button
                      startIcon={<DeleteIcon />}
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteUser(user._id);
                      }}
                    >
                      Delete
                    </Button>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1">User Details:</Typography>
                  <Typography>Email: {user.email}</Typography>
                  <Typography>Website: {user.website}</Typography>
                </Box>
                
                <Typography variant="subtitle1" gutterBottom>Chatbots:</Typography>
                {user.chatbots && user.chatbots.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {user.chatbots.map((chatbot) => (
                      <Chip
                        key={chatbot._id}
                        label={`${chatbot.name} (ID: ${chatbot._id})`}
                        color={chatbot.isActive ? "primary" : "default"}
                        sx={{ m: 0.5 }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography color="text.secondary">No chatbots found</Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Chatbots</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenCreateChatbotDialog(true)}
            >
              Create Chatbot
            </Button>
          </Box>
          <Grid container spacing={2}>
            {chatbots.map((chatbot) => (
              <Grid item xs={12} sm={6} md={4} key={chatbot._id}>
                <Paper 
                  sx={{ 
                    p: 2,
                    bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">{chatbot.name}</Typography>
                    <Box>
                      <Button
                        startIcon={<EditIcon />}
                        onClick={() => handleEditChatbot(chatbot)}
                        sx={{ mr: 1 }}
                      >
                        Edit
                      </Button>
                      <Button
                        startIcon={<DeleteIcon />}
                        color="error"
                        onClick={() => handleDeleteChatbot(chatbot._id)}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Business: {getBusinessName(chatbot.businessId)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Status: {chatbot.isActive ? 'Active' : 'Inactive'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Trial: {chatbot.trialStatus.isInTrial ? 'Active' : 'Expired'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Trial End: {new Date(chatbot.trialStatus.trialEndDate).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Position: {chatbot.position}
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        bgcolor: chatbot.primaryColor,
                        borderRadius: '50%'
                      }}
                    />
                    <Typography variant="body2">
                      Color: {chatbot.primaryColor}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6">Contact Messages</Typography>
          </Box>
          {messagesError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {messagesError}
            </Alert>
          )}
          <TableContainer component={Paper} sx={{ 
            bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white'
          }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {messages.map((message) => (
                  <React.Fragment key={message._id}>
                    <TableRow>
                      <TableCell>
                        {new Date(message.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{message.name}</TableCell>
                      <TableCell>{message.email}</TableCell>
                      <TableCell>{message.subject}</TableCell>
                      <TableCell>
                        <Chip
                          label={message.status}
                          color={
                            message.status === 'new'
                              ? 'primary'
                              : message.status === 'read'
                              ? 'default'
                              : 'success'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleExpandMessage(message._id)}
                        >
                          {expandedMessage === message._id ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteMessage(message._id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                        <Collapse in={expandedMessage === message._id} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 1 }}>
                            <Typography variant="h6" gutterBottom component="div">
                              Message Content
                            </Typography>
                            <Typography variant="body1" paragraph>
                              {message.message}
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                  value={message.status}
                                  label="Status"
                                  onChange={(e: SelectChangeEvent) =>
                                    handleUpdateMessageStatus(message._id, e.target.value as 'read' | 'replied')
                                  }
                                >
                                  <MenuItem value="new">New</MenuItem>
                                  <MenuItem value="read">Read</MenuItem>
                                  <MenuItem value="replied">Replied</MenuItem>
                                </Select>
                              </FormControl>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6">Bulk Edit Keyword Responses</Typography>
          </Box>
          <Paper 
            sx={{ 
              p: 3, 
              mb: 2,
              bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white'
            }}
          >
            <Paper sx={{ p: 2, mb: 2, bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white' }}>
              <TextField
                fullWidth
                label="Search Business"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Paper>

            {searchQuery && (
              <Paper sx={{ p: 2, mb: 2, bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Select Business:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {filteredUsers.map((user) => (
                    <Chip
                      key={user._id}
                      label={user.businessName}
                      onClick={() => handleBusinessSelect(user)}
                      color={selectedBusiness?._id === user._id ? 'primary' : 'default'}
                    />
                  ))}
                </Box>
              </Paper>
            )}

            {selectedBusiness && (
              <Paper sx={{ p: 2, mb: 2, bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Select Chatbot:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedBusiness.chatbots.map((chatbot) => (
                    <Chip
                      key={chatbot._id}
                      label={chatbot.name}
                      onClick={() => handleChatbotSelect(chatbot)}
                      color={selectedChatbot?._id === chatbot._id ? 'primary' : 'default'}
                    />
                  ))}
                </Box>
              </Paper>
            )}

            {selectedChatbot && (
              <Paper sx={{ p: 2, bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Current Responses:
                </Typography>
                <Box sx={{ mb: 2, maxHeight: '200px', overflow: 'auto' }}>
                  {responses.map((response, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        mb: 2, 
                        p: 1, 
                        bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'grey.100', 
                        borderRadius: 1,
                        border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.12)' : 'none'
                      }}
                    >
                      <Typography variant="body2">
                        <strong>Keywords:</strong> {response.keywords.join(', ')}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Response:</strong> {response.response}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Variations:</strong> {response.responseVariations.join(', ')}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Variables:</strong> {response.variables.map(v => v.name).join(', ')}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Typography variant="subtitle1" gutterBottom>
                  New Responses (Paste here):
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={10}
                  value={newResponses}
                  onChange={(e) => setNewResponses(e.target.value)}
                  placeholder={`Format:
Keyword: keyword1, keyword2, keyword3
Response: Your response text
Variations: variation1, variation2
Variables: variable1, variable2

Keyword: another1, another2
Response: Another response text
Variations: var1, var2
Variables: var3, var4`}
                  sx={{ mb: 2 }}
                />

                {bulkEditError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {bulkEditError}
                  </Alert>
                )}

                {bulkEditSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {bulkEditSuccess}
                  </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveResponses}
                    disabled={!newResponses.trim()}
                  >
                    Save Responses
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => {
                      const formattedResponses = responses.map(r => 
                        `Keyword: ${r.keywords.join(', ')}\nResponse: ${r.response}\nVariations: ${r.responseVariations.join(', ')}\nVariables: ${r.variables.map(v => v.name).join(', ')}`
                      ).join('\n\n');
                      navigator.clipboard.writeText(formattedResponses);
                    }}
                  >
                    Copy Current
                  </Button>
                </Box>
              </Paper>
            )}
          </Paper>
        </TabPanel>
      </Container>

      {/* Dialog for editing users */}
      <Dialog open={openUserDialog} onClose={() => setOpenUserDialog(false)}>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <TextField
            margin="normal"
            fullWidth
            label="Business Name"
            name="businessName"
            value={userFormData.businessName}
            onChange={handleUserChange}
          />
          <TextField
            margin="normal"
            fullWidth
            label="Website"
            name="website"
            value={userFormData.website}
            onChange={handleUserChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUserDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateUser} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for editing chatbots */}
      <Dialog open={openChatbotDialog} onClose={() => setOpenChatbotDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Chatbot</DialogTitle>
        <DialogContent>
          <TextField
            margin="normal"
            fullWidth
            label="Name"
            name="name"
            value={chatbotFormData.name}
            onChange={handleChatbotChange}
          />
          <TextField
            margin="normal"
            fullWidth
            label="Welcome Message"
            name="welcomeMessage"
            value={chatbotFormData.welcomeMessage}
            onChange={handleChatbotChange}
            multiline
            rows={3}
          />
          <TextField
            margin="normal"
            fullWidth
            label="Primary Color"
            name="primaryColor"
            type="color"
            value={chatbotFormData.primaryColor}
            onChange={handleChatbotChange}
          />
          <TextField
            margin="normal"
            fullWidth
            select
            label="Position"
            name="position"
            value={chatbotFormData.position}
            onChange={handleChatbotChange}
          >
            <MenuItem value="bottom-right">Bottom Right</MenuItem>
            <MenuItem value="bottom-left">Bottom Left</MenuItem>
          </TextField>
          
          {/* Icon Type Selection */}
          <TextField
            margin="normal"
            fullWidth
            select
            label="Chat Button Icon"
            name="iconType"
            value={chatbotFormData.iconType || 'default'}
            onChange={handleChatbotChange}
            helperText="Select the icon to display on the chat button"
          >
            <MenuItem value="default">Default Chat Bubble</MenuItem>
            <MenuItem value="agent">Customer Service Agent</MenuItem>
            <MenuItem value="emoji-smile">Smile Emoji</MenuItem>
            <MenuItem value="emoji-wave">Wave Emoji</MenuItem>
            <MenuItem value="question">Question Mark</MenuItem>
            <MenuItem value="support">Support Icon</MenuItem>
            <MenuItem value="custom">Custom Icon URL</MenuItem>
          </TextField>
          
          {chatbotFormData.iconType === 'custom' && (
            <TextField
              margin="normal"
              fullWidth
              label="Custom Icon URL"
              name="customIconUrl"
              value={chatbotFormData.customIconUrl}
              onChange={handleChatbotChange}
              helperText="Enter the URL of your custom icon (recommended size: 32x32px)"
            />
          )}
          
          <FormControlLabel
            control={
              <Checkbox
                checked={chatbotFormData.isActive}
                onChange={handleChatbotChange}
                name="isActive"
              />
            }
            label="Active"
          />
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Trial Settings</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={chatbotFormData.trialStatus.isInTrial}
                onChange={(e) => {
                  setChatbotFormData(prev => ({
                    ...prev,
                    trialStatus: {
                      ...prev.trialStatus,
                      isInTrial: e.target.checked
                    }
                  }));
                }}
                name="trialStatus.isInTrial"
              />
            }
            label="Enable Trial"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 2 }}>
            Trial will automatically end 14 days after the first API call
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Business Hours</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={chatbotFormData.settings.businessHours.enabled}
                onChange={handleChatbotChange}
                name="settings.businessHours.enabled"
              />
            }
            label="Enable Business Hours"
          />
          <TextField
            margin="normal"
            fullWidth
            label="Start Time"
            name="settings.businessHours.start"
            type="time"
            value={chatbotFormData.settings.businessHours.start}
            onChange={handleChatbotChange}
            InputLabelProps={{ shrink: true }}
            disabled={!chatbotFormData.settings.businessHours.enabled}
          />
          <TextField
            margin="normal"
            fullWidth
            label="End Time"
            name="settings.businessHours.end"
            type="time"
            value={chatbotFormData.settings.businessHours.end}
            onChange={handleChatbotChange}
            InputLabelProps={{ shrink: true }}
            disabled={!chatbotFormData.settings.businessHours.enabled}
          />
          <TextField
            margin="normal"
            fullWidth
            label="Timezone"
            name="settings.businessHours.timezone"
            value={chatbotFormData.settings.businessHours.timezone}
            onChange={handleChatbotChange}
            disabled={!chatbotFormData.settings.businessHours.enabled}
          />
          
          {/* Add Sound Settings after Business Hours */}
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Sound Settings</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={chatbotFormData.settings.soundSettings.enabled}
                onChange={handleChatbotChange}
                name="settings.soundSettings.enabled"
              />
            }
            label="Enable sounds for end users by default"
          />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <TextField
              margin="normal"
              fullWidth
              select
              label="Message Sent Sound"
              name="settings.soundSettings.messageSentSound"
              value={chatbotFormData.settings.soundSettings.messageSentSound || 'pop'}
              onChange={handleChatbotChange}
              disabled={!chatbotFormData.settings.soundSettings.enabled}
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="newmessage">New Message</MenuItem>
              <MenuItem value="pop">Pop</MenuItem>
              <MenuItem value="hospitalbell">Hospital Bell</MenuItem>
              <MenuItem value="dingdingding">Ding Ding Ding</MenuItem>
              <MenuItem value="dududu">Dududu</MenuItem>
              <MenuItem value="notification">Notification</MenuItem>
              <MenuItem value="8bit">8-bit</MenuItem>
            </TextField>
            <IconButton 
              size="small" 
              onClick={() => playSound(chatbotFormData.settings.soundSettings.messageSentSound || 'pop')}
              disabled={!chatbotFormData.settings.soundSettings.enabled || chatbotFormData.settings.soundSettings.messageSentSound === 'none'}
              sx={{ mt: 2, bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' } }}
            >
              <PlayArrowIcon />
            </IconButton>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              margin="normal"
              fullWidth
              select
              label="Message Received Sound"
              name="settings.soundSettings.messageReceivedSound"
              value={chatbotFormData.settings.soundSettings.messageReceivedSound || 'notification'}
              onChange={handleChatbotChange}
              disabled={!chatbotFormData.settings.soundSettings.enabled}
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="newmessage">New Message</MenuItem>
              <MenuItem value="pop">Pop</MenuItem>
              <MenuItem value="hospitalbell">Hospital Bell</MenuItem>
              <MenuItem value="dingdingding">Ding Ding Ding</MenuItem>
              <MenuItem value="dududu">Dududu</MenuItem>
              <MenuItem value="notification">Notification</MenuItem>
              <MenuItem value="8bit">8-bit</MenuItem>
            </TextField>
            <IconButton 
              size="small" 
              onClick={() => playSound(chatbotFormData.settings.soundSettings.messageReceivedSound || 'notification')}
              disabled={!chatbotFormData.settings.soundSettings.enabled || chatbotFormData.settings.soundSettings.messageReceivedSound === 'none'}
              sx={{ mt: 2, bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' } }}
            >
              <PlayArrowIcon />
            </IconButton>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            End users will be able to toggle sounds on/off in the widget, but will default to your setting.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenChatbotDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateChatbot} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for creating users */}
      <Dialog open={openCreateUserDialog} onClose={() => setOpenCreateUserDialog(false)}>
        <DialogTitle>Create Business User</DialogTitle>
        <DialogContent>
          <TextField
            margin="normal"
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={createUserFormData.email}
            onChange={handleCreateUserChange}
            required
          />
          <TextField
            margin="normal"
            fullWidth
            label="Password"
            name="password"
            type="password"
            value={createUserFormData.password}
            onChange={handleCreateUserChange}
            required
          />
          <TextField
            margin="normal"
            fullWidth
            label="Business Name"
            name="businessName"
            value={createUserFormData.businessName}
            onChange={handleCreateUserChange}
            required
          />
          <TextField
            margin="normal"
            fullWidth
            label="Website"
            name="website"
            value={createUserFormData.website}
            onChange={handleCreateUserChange}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateUserDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for creating chatbots */}
      <Dialog open={openCreateChatbotDialog} onClose={() => setOpenCreateChatbotDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Chatbot</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
            <TextField
              margin="normal"
              fullWidth
              label="Business ID"
              name="businessId"
              value={createChatbotFormData.businessId}
              onChange={(e) => {
                setCreateChatbotFormData(prev => ({
                  ...prev,
                  businessId: e.target.value
                }));
              }}
              required
              helperText="Enter the exact business ID to assign this chatbot to"
            />
            {selectedBusiness && (
              <Button 
                sx={{ mt: 3, ml: 1 }}
                variant="outlined"
                size="small"
                onClick={() => {
                  setCreateChatbotFormData(prev => ({
                    ...prev,
                    businessId: selectedBusiness._id
                  }));
                }}
              >
                Use Selected
              </Button>
            )}
          </Box>

          <TextField
            margin="normal"
            fullWidth
            label="Name"
            name="name"
            value={createChatbotFormData.name}
            onChange={handleCreateChatbotChange}
            required
          />
          <TextField
            margin="normal"
            fullWidth
            label="Welcome Message"
            name="welcomeMessage"
            value={createChatbotFormData.welcomeMessage}
            onChange={handleCreateChatbotChange}
            multiline
            rows={3}
            required
          />
          <TextField
            margin="normal"
            fullWidth
            label="Primary Color"
            name="primaryColor"
            type="color"
            value={createChatbotFormData.primaryColor}
            onChange={handleCreateChatbotChange}
            required
          />
          <TextField
            margin="normal"
            fullWidth
            select
            label="Position"
            name="position"
            value={createChatbotFormData.position}
            onChange={handleCreateChatbotChange}
            required
          >
            <MenuItem value="bottom-right">Bottom Right</MenuItem>
            <MenuItem value="bottom-left">Bottom Left</MenuItem>
          </TextField>
          
          {/* Widget Shape Selection */}
          <TextField
            margin="normal"
            fullWidth
            select
            label="Widget Shape"
            name="widgetShape"
            value={createChatbotFormData.widgetShape || 'circle'}
            onChange={handleCreateChatbotChange}
            required
          >
            <MenuItem value="circle">Circle</MenuItem>
            <MenuItem value="bubble">Chat Bubble</MenuItem>
          </TextField>
          
          {/* Font Family Selection */}
          <TextField
            margin="normal"
            fullWidth
            select
            label="Font Family"
            name="fontFamily"
            value={createChatbotFormData.fontFamily || 'Roboto'}
            onChange={handleCreateChatbotChange}
            required
          >
            <MenuItem value="Roboto">Roboto</MenuItem>
            <MenuItem value="Open Sans">Open Sans</MenuItem>
            <MenuItem value="Lato">Lato</MenuItem>
            <MenuItem value="Montserrat">Montserrat</MenuItem>
            <MenuItem value="Playfair Display">Playfair Display</MenuItem>
            <MenuItem value="Poppins">Poppins</MenuItem>
            <MenuItem value="Inter">Inter</MenuItem>
          </TextField>
          
          {/* Icon Type Selection */}
          <TextField
            margin="normal"
            fullWidth
            select
            label="Chat Button Icon"
            name="iconType"
            value={createChatbotFormData.iconType || 'default'}
            onChange={handleCreateChatbotChange}
            required
          >
            <MenuItem value="default">Default Chat Bubble</MenuItem>
            <MenuItem value="agent">Customer Service Agent</MenuItem>
            <MenuItem value="emoji-smile">Smile Emoji</MenuItem>
            <MenuItem value="emoji-wave">Wave Emoji</MenuItem>
            <MenuItem value="question">Question Mark</MenuItem>
            <MenuItem value="support">Support Icon</MenuItem>
            <MenuItem value="custom">Custom Icon URL</MenuItem>
          </TextField>
          
          {createChatbotFormData.iconType === 'custom' && (
            <TextField
              margin="normal"
              fullWidth
              label="Custom Icon URL"
              name="customIconUrl"
              value={createChatbotFormData.customIconUrl}
              onChange={handleCreateChatbotChange}
              helperText="Enter the URL of your custom icon (recommended size: 32x32px)"
            />
          )}
          
          <FormControlLabel
            control={
              <Checkbox
                checked={createChatbotFormData.isActive}
                onChange={handleCreateChatbotChange}
                name="isActive"
              />
            }
            label="Active"
          />
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Trial Settings</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={createChatbotFormData.trialStatus.isInTrial}
                onChange={(e) => {
                  setCreateChatbotFormData(prev => ({
                    ...prev,
                    trialStatus: {
                      ...prev.trialStatus,
                      isInTrial: e.target.checked
                    }
                  }));
                }}
                name="trialStatus.isInTrial"
              />
            }
            label="Enable Trial"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 2 }}>
            Trial will automatically end 14 days after the first API call
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>AI Settings</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={createChatbotFormData.settings.aiSettings.enabled}
                onChange={(e) => {
                  setCreateChatbotFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      aiSettings: {
                        ...prev.settings.aiSettings,
                        enabled: e.target.checked
                      }
                    }
                  }));
                }}
                name="settings.aiSettings.enabled"
              />
            }
            label="Enable AI Responses"
          />
          
          {createChatbotFormData.settings.aiSettings.enabled && (
            <>
              <TextField
                margin="normal"
                fullWidth
                label="AI Model"
                name="settings.aiSettings.model"
                select
                value={createChatbotFormData.settings.aiSettings.model}
                onChange={handleCreateChatbotChange}
              >
                <MenuItem value="gpt-3.5-turbo">GPT-3.5 Turbo</MenuItem>
                <MenuItem value="gpt-4">GPT-4</MenuItem>
              </TextField>
              
              <TextField
                margin="normal"
                fullWidth
                label="Custom Instructions"
                name="settings.aiSettings.customInstructions"
                multiline
                rows={3}
                value={createChatbotFormData.settings.aiSettings.customInstructions}
                onChange={handleCreateChatbotChange}
                helperText="Instructions to guide the AI's responses (optional)"
              />
              
              <TextField
                margin="normal"
                fullWidth
                label="Temperature"
                name="settings.aiSettings.temperature"
                type="number"
                inputProps={{ min: 0, max: 2, step: 0.1 }}
                value={createChatbotFormData.settings.aiSettings.temperature}
                onChange={handleCreateChatbotChange}
                helperText="Controls randomness (0-2). Lower values are more focused, higher values more creative."
              />
              
              <TextField
                margin="normal"
                fullWidth
                label="Max Tokens"
                name="settings.aiSettings.maxTokens"
                type="number"
                inputProps={{ min: 100, max: 4000, step: 1 }}
                value={createChatbotFormData.settings.aiSettings.maxTokens}
                onChange={handleCreateChatbotChange}
                helperText="Maximum length of AI responses"
              />
            </>
          )}
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Quick Replies</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={createChatbotFormData.settings.defaultQuickReplies.enabled}
                onChange={(e) => {
                  setCreateChatbotFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      defaultQuickReplies: {
                        ...prev.settings.defaultQuickReplies,
                        enabled: e.target.checked
                      }
                    }
                  }));
                }}
                name="settings.defaultQuickReplies.enabled"
              />
            }
            label="Enable Default Quick Replies"
          />
          
          {createChatbotFormData.settings.defaultQuickReplies.enabled && (
            <>
              <TextField
                margin="normal"
                fullWidth
                label="Quick Replies (comma separated)"
                name="quickRepliesText"
                value={createChatbotFormData.settings.defaultQuickReplies.replies.join(', ')}
                onChange={(e) => {
                  const repliesArray = e.target.value.split(',').map(reply => reply.trim()).filter(Boolean);
                  setCreateChatbotFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      defaultQuickReplies: {
                        ...prev.settings.defaultQuickReplies,
                        replies: repliesArray
                      }
                    }
                  }));
                }}
                helperText="Add common questions or phrases for users to click"
              />
              
              <TextField
                margin="normal"
                fullWidth
                label="Max Display"
                name="settings.defaultQuickReplies.maxDisplay"
                type="number"
                inputProps={{ min: 1, max: 10, step: 1 }}
                value={createChatbotFormData.settings.defaultQuickReplies.maxDisplay || 3}
                onChange={handleCreateChatbotChange}
                helperText="Maximum number of quick replies to show"
              />
            </>
          )}
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Leave Message Feature</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={createChatbotFormData.settings.allowLeaveMessage}
                onChange={(e) => {
                  setCreateChatbotFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      allowLeaveMessage: e.target.checked
                    }
                  }));
                }}
                name="settings.allowLeaveMessage"
              />
            }
            label="Allow users to leave messages"
          />
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>File Upload</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={createChatbotFormData.settings.allowFileUpload}
                onChange={(e) => {
                  setCreateChatbotFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      allowFileUpload: e.target.checked
                    }
                  }));
                }}
                name="settings.allowFileUpload"
              />
            }
            label="Allow users to upload files"
          />
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Business Hours</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={createChatbotFormData.settings.businessHours.enabled}
                onChange={(e) => {
                  setCreateChatbotFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      businessHours: {
                        ...prev.settings.businessHours,
                        enabled: e.target.checked
                      }
                    }
                  }));
                }}
                name="settings.businessHours.enabled"
              />
            }
            label="Enable Business Hours"
          />
          
          {createChatbotFormData.settings.businessHours.enabled && (
            <>
              <TextField
                margin="normal"
                fullWidth
                label="Start Time"
                name="settings.businessHours.start"
                type="time"
                value={createChatbotFormData.settings.businessHours.start}
                onChange={handleCreateChatbotChange}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                margin="normal"
                fullWidth
                label="End Time"
                name="settings.businessHours.end"
                type="time"
                value={createChatbotFormData.settings.businessHours.end}
                onChange={handleCreateChatbotChange}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                margin="normal"
                fullWidth
                label="Timezone"
                name="settings.businessHours.timezone"
                value={createChatbotFormData.settings.businessHours.timezone}
                onChange={handleCreateChatbotChange}
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createChatbotFormData.settings.businessHours.allowLeaveMessageOutsideHours}
                    onChange={(e) => {
                      setCreateChatbotFormData(prev => ({
                        ...prev,
                        settings: {
                          ...prev.settings,
                          businessHours: {
                            ...prev.settings.businessHours,
                            allowLeaveMessageOutsideHours: e.target.checked
                          }
                        }
                      }));
                    }}
                    name="settings.businessHours.allowLeaveMessageOutsideHours"
                  />
                }
                label="Allow leaving messages outside business hours"
              />
            </>
          )}
          
          {/* Sound Settings */}
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Sound Settings</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={createChatbotFormData.settings.soundSettings.enabled}
                onChange={(e) => {
                  setCreateChatbotFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      soundSettings: {
                        ...prev.settings.soundSettings,
                        enabled: e.target.checked
                      }
                    }
                  }));
                }}
                name="settings.soundSettings.enabled"
              />
            }
            label="Enable sounds for end users by default"
          />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <TextField
              margin="normal"
              fullWidth
              select
              label="Message Sent Sound"
              name="settings.soundSettings.messageSentSound"
              value={createChatbotFormData.settings.soundSettings.messageSentSound || 'pop'}
              onChange={handleCreateChatbotChange}
              disabled={!createChatbotFormData.settings.soundSettings.enabled}
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="newmessage">New Message</MenuItem>
              <MenuItem value="pop">Pop</MenuItem>
              <MenuItem value="hospitalbell">Hospital Bell</MenuItem>
              <MenuItem value="dingdingding">Ding Ding Ding</MenuItem>
              <MenuItem value="dududu">Dududu</MenuItem>
              <MenuItem value="notification">Notification</MenuItem>
              <MenuItem value="8bit">8-bit</MenuItem>
            </TextField>
            <IconButton 
              size="small" 
              onClick={() => playSound(createChatbotFormData.settings.soundSettings.messageSentSound || 'pop')}
              disabled={!createChatbotFormData.settings.soundSettings.enabled || createChatbotFormData.settings.soundSettings.messageSentSound === 'none'}
              sx={{ mt: 2, bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' } }}
            >
              <PlayArrowIcon />
            </IconButton>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              margin="normal"
              fullWidth
              select
              label="Message Received Sound"
              name="settings.soundSettings.messageReceivedSound"
              value={createChatbotFormData.settings.soundSettings.messageReceivedSound || 'notification'}
              onChange={handleCreateChatbotChange}
              disabled={!createChatbotFormData.settings.soundSettings.enabled}
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="newmessage">New Message</MenuItem>
              <MenuItem value="pop">Pop</MenuItem>
              <MenuItem value="hospitalbell">Hospital Bell</MenuItem>
              <MenuItem value="dingdingding">Ding Ding Ding</MenuItem>
              <MenuItem value="dududu">Dududu</MenuItem>
              <MenuItem value="notification">Notification</MenuItem>
              <MenuItem value="8bit">8-bit</MenuItem>
            </TextField>
            <IconButton 
              size="small" 
              onClick={() => playSound(createChatbotFormData.settings.soundSettings.messageReceivedSound || 'notification')}
              disabled={!createChatbotFormData.settings.soundSettings.enabled || createChatbotFormData.settings.soundSettings.messageReceivedSound === 'none'}
              sx={{ mt: 2, bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' } }}
            >
              <PlayArrowIcon />
            </IconButton>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateChatbotDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateChatbot} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default AdminDashboard; 